/**
 * Phase-gates feature domain types (PX-W6.3, TASK-EDD04F2F).
 *
 * Field names mirror the backend views VERBATIM (`PhaseGateView`,
 * `ProjectPhaseView`, `PhaseCrosswalk.PhaseLine`, `ConfigPayloadView`,
 * `MongoProjectConfigSnapshot`) — Jackson `NON_NULL`, so absent ≠ null.
 * Ageing fields are NUMBERS rendered as-is; the client derives no verdict
 * (INV-3: no slipping/overdue/health math on this side of the wire).
 */

/** Derived gate state (`PhaseGateCatalog.GateStatus`, lower-kebab on the wire). */
export type GateStatus = string

/** One evaluated criterion (`PhaseGateView.CriterionView`). */
export interface GateCriterion {
  key: string
  text: string
  evidenceKind?: string
  /** `MET` / `NOT_MET` / `PENDING_EVIDENCE`. */
  state?: string
  note?: string
}

/** One decision record off the audit trail (`PhaseGateView.GateRecordView`). */
export interface GateRecord {
  action: string
  occurredAt: number
  actorUserId?: string
  actorName?: string
  /** `APPROVE` / `RETURN`, absent on submit rows. */
  decision?: string
  comments?: string
  overridden: boolean
  overrideReason?: string
  changeRequestId?: string
  resolvesCr?: string
  lockedConfigVersion?: string
}

/** The computed read model of one phase gate. */
export interface PhaseGateView {
  /** `flow.01` .. `flow.04`. */
  ref: string
  /** e.g. `STEP 01`. */
  step: string
  title: string
  gateTxt?: string
  scope?: string
  type?: string
  status: GateStatus
  open: boolean
  criteria?: GateCriterion[]
  criteriaAllMet: boolean
  prerequisiteRef?: string
  prerequisiteSatisfied?: boolean
  blocks?: string
  /** Epoch millis; absent pre-decision on NON_NULL rows. */
  openSince?: number
  /** Whole days open, `0` once approved. A number, never a verdict. */
  ageDays?: number
  decision?: string
  decidedBy?: string
  decidedByName?: string
  decidedAt?: number
  comments?: string
  overridden: boolean
  overrideReason?: string
  changeRequestId?: string
  resolvesCr?: string
  lockedConfigVersion?: string
  submittedBy?: string
  /** Populated by the single-gate GET only. */
  records?: GateRecord[]
}

/** One crosswalk row — both lifecycles, rendered verbatim (INV-2). */
export interface CrosswalkLine {
  phase: string
  name: string
  subLabel?: string
  gateLabel?: string
  /** The Rev-A engagement-phase label; absent when the sources map none. */
  revAPhase?: string
  flowStep?: string
  gateRef?: string
  /** Where the two vocabularies disagree — shown muted, never resolved by fiat. */
  mappingNote?: string
}

/** One phase-history entry (`ProjectPhaseDates`, oldest first). */
export interface PhaseDateEntry {
  phase?: string
  enteredAt?: number
  plannedEnd?: number
  actualEnd?: number
}

/** The computed phase view (`GET …/phase`). */
export interface ProjectPhaseView {
  projectId: string
  /** Absent before the engagement enters a phase (no eighth "not started" value). */
  currentPhase?: string
  crosswalk?: CrosswalkLine
  enteredAt?: number
  plannedEnd?: number
  /** Whole days in the current phase. A number, never a verdict. */
  phaseAgeDays: number
  phaseDates?: PhaseDateEntry[]
}

/**
 * The decision vocabulary, as ONE client-side source of truth.
 *
 * `APPROVE` is spelled with TWO Ps because that is the server's literal —
 * `PhaseGateService.DECISION_APPROVE = "APPROVE"` — and the decide endpoint
 * uppercases + trims but never repairs a missing P: a one-P body is refused
 * with `phase.gate.decision.invalid`. `gateDecisionContract.test.ts` pins
 * these literals against the Java source in-process, so this class of typo
 * can never survive a test run again.
 */
export const GATE_DECISIONS = ['APPROVE', 'RETURN'] as const

/** One decision value — every caller types against this, never a fresh literal. */
export type GateDecision = (typeof GATE_DECISIONS)[number]

/** Body of `POST …/gates/:gateRef/decision` (`GateDecisionRequest`). */
export interface GateDecisionInput {
  decision: GateDecision
  comments?: string
  override?: boolean
  overrideReason?: string
  lockedConfigVersion?: string
  resolvesCr?: string
  payloadDigest?: string
}

/** Body of `POST …/phase` (`PhaseTransitionRequest`). */
export interface PhaseTransitionInput {
  toPhase: string
  plannedEnd?: number
  override?: boolean
  overrideReason?: string
}

/** One surface inside the config payload (`ConfigSurfaceSnapshot`). */
export interface ConfigSurfaceSnapshot {
  surface: string
  label?: string
  present: boolean
  note?: string
  fields?: Record<string, unknown>
  items?: Record<string, unknown>[]
}

/**
 * The flow.04 review payload (`GET …/gates/:gateRef/payload`). `locked` is the
 * load-bearing field: live render (`locked=false`) before approval, frozen
 * version after — later reads return the recorded bytes, never a re-render.
 */
export interface ConfigPayloadView {
  gateRef: string
  locked: boolean
  version?: number
  lockedBy?: string
  lockedByName?: string
  lockedAt: number
  /** SHA-256 over the canonical surfaces JSON — present on live renders too. */
  payloadDigest?: string
  surfaces?: ConfigSurfaceSnapshot[]
}

/** One locked configuration version (`MongoProjectConfigSnapshot` projection). */
export interface ConfigSnapshotRow {
  id?: string
  version: number
  lockedBy?: string
  lockedByName?: string
  /** Epoch millis. */
  lockedAt: number
  payloadDigest?: string
}
